import { useNavigate } from "react-router-dom";
import { useTranslation } from "@/hooks/useTranslation";
import { getLoginPath, getHomePath, getPricingPath, getSettingsPath } from "@/utils/localePaths";
import { useAuth } from "@/contexts/AuthContext";
import { Link } from "react-router-dom";
import { User, Settings, LogOut, ArrowLeft } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import Footer from "@/components/Footer";

function TermsPage() {
  const navigate = useNavigate();
  const { locale } = useTranslation();
  const { user, logout } = useAuth();

  const getPlanDisplayName = (planType) => {
    const planNames = {
      'free': 'Free',
      'pro_monthly': 'Pro Monthly',
      'business_monthly': 'Business Monthly',
      'persona_staff': 'Staff'
    };
    return planNames[planType] || planType;
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-b from-black via-gray-900 to-black">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-gradient-to-b from-black via-gray-900 to-black pt-2 pb-1 px-4">
        <div className="mb-1 px-4">
          <div className="flex justify-between items-center gap-4 bg-gray-900/50 backdrop-blur-sm rounded-lg p-2 border border-gray-800">
            <div 
              className="cursor-pointer hover:opacity-80 transition-opacity"
              onClick={() => navigate(getHomePath(locale))}
            >
              <img 
                src="/opara-logo-trimmed.png" 
                alt="OPARA Logo" 
                className="h-10 w-auto object-contain"
              />
            </div>
            
            {user && (
              <div className="flex items-center gap-2">
                <div className="text-sm bg-gray-800 px-3 py-1 rounded-lg border border-gray-700">
                  <span className="text-white">Credits: </span>
                  <span className="text-white font-bold">
                    {user?.plan_type === "persona_staff" ? "∞" : user?.credits_remaining}
                  </span>
                </div>
                
                <Link to={getPricingPath(locale)}>
                  <Button className="bg-[#FFDD59] hover:bg-[#E6C84D] text-black font-bold py-1 text-sm">
                    Upgrade
                  </Button>
                </Link>
                
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="w-8 h-8 rounded-full bg-gradient-to-br from-[#FFDD59] to-[#E6C84D] flex items-center justify-center hover:opacity-90 transition-opacity cursor-pointer border-2 border-gray-700 hover:border-[#FFDD59]">
                      <User className="w-4 h-4 text-black" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="w-56 bg-gray-900 border-gray-700" align="end">
                    <DropdownMenuLabel className="text-white">
                      <div className="flex flex-col space-y-1">
                        <p className="text-sm font-medium">
                          {user?.first_name} {user?.last_name}
                        </p>
                        <p className="text-xs text-gray-400">
                          Plan: <span className="text-[#FFDD59]">{getPlanDisplayName(user?.plan_type)}</span>
                        </p>
                      </div>
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator className="bg-gray-700" />
                    <DropdownMenuItem asChild className="cursor-pointer text-gray-300 hover:text-white hover:bg-gray-800">
                      <Link to={getSettingsPath(locale)} className="flex items-center">
                        <Settings className="w-4 h-4 mr-2" />
                        Account Settings
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator className="bg-gray-700" />
                    <DropdownMenuItem onClick={logout} className="cursor-pointer text-red-400 hover:text-red-300 hover:bg-gray-800">
                      <LogOut className="w-4 h-4 mr-2" />
                      Sign Out
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            )}

            {!user && (
              <div className="flex items-center gap-2">
                <Link to={getPricingPath(locale)}>
                  <Button className="bg-[#FFDD59] hover:bg-[#FFE875] text-black font-bold w-40 py-1 text-sm">
                    Pricing
                  </Button>
                </Link>
                <Link to={getLoginPath(locale)}>
                  <Button className="bg-orange-500 hover:bg-orange-600 text-white font-bold w-40 py-1 text-sm">
                    SIGN IN
                  </Button>
                </Link>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="relative z-10 px-4 py-4">
        <div className="max-w-[720px] mx-auto">

          {/* Hero - Back Button + Logo */}
          <div className="flex items-start gap-4 mb-6">
            <button 
              onClick={() => navigate(-1)}
              className="flex items-center gap-1.5 text-gray-500 hover:text-[#FFDD59] transition-colors text-sm mt-1"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Back</span>
            </button>
            
            <div className="flex-1 text-center">
              <img 
                src="/opara-logo-trimmed.png" 
                alt="OPARA" 
                className="h-10 w-auto mx-auto"
              />
            </div>
            
            <div className="w-[52px]"></div>
          </div>

          {/* Content Sections */}
          <div className="space-y-5">
            
            {/* Title Section */}
            <section className="border-l-2 border-[#FFDD59] pl-4 mb-6">
              <h1 className="text-base font-semibold text-[#FFDD59] mb-2">
                Terms of Service
              </h1>
              <p className="text-sm text-gray-500">
                Last Updated: 21 July 2026
              </p>
            </section>

            {/* Section 1 */}
            <section className="bg-gray-900/30 rounded-lg px-4 py-3 border border-gray-800/40">
              <h2 className="text-sm font-semibold text-white mb-2">
                1. Agreement Acceptance
              </h2>
              <p className="text-sm text-gray-400 leading-relaxed">
                By accessing or using the Site (opara.co) and/or the Services provided by OPARA, you agree to be bound by these Terms of Service ("Terms"). If you are using the Services on behalf of a legal entity, you represent that you have the authority to bind that entity to these Terms. If you do not agree to these Terms, you may not use the Site or the Services.
              </p>
            </section>

            {/* Section 2 */}
            <section className="bg-gray-900/30 rounded-lg px-4 py-3 border border-gray-800/40">
              <h2 className="text-sm font-semibold text-white mb-2">
                2. Parties and Definitions
              </h2>
              <p className="text-sm text-gray-400 leading-relaxed">
                This Agreement is between <span className="text-gray-300">Persona Event And Marketing Solutions FZ LLE</span> ("Company," "Persona," "we," or "us"), a company registered in the United Arab Emirates (Office: Twin Towers, M1 Floor, Creative City, Fujairah Media Free Zone, Fujairah), and the user ("you"). The Site (opara.co) and its associated analysis tools are collectively the "Services." "Credits" are the digital access units described in Section 3.
              </p>
            </section>

            {/* Section 3 */}
            <section className="bg-gray-900/30 rounded-lg px-4 py-3 border border-gray-800/40">
              <h2 className="text-sm font-semibold text-white mb-2">
                3. Services & Credits
              </h2>
              <p className="text-sm text-gray-400 leading-relaxed mb-3">
                OPARA provides AI-driven influencer marketing ROI projections through a digital Credit system.
              </p>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-[#FFDD59]">•</span>
                  <span className="text-gray-400"><span className="text-gray-300">Credits.</span> 1 Credit = 1 influencer analysis (query or report).</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#FFDD59]">•</span>
                  <span className="text-gray-400"><span className="text-gray-300">Pricing.</span> All fees are stated in US Dollars (USD). Persona may adjust pricing or the Credit cost of specific features on a prospective basis at any time; such changes do not affect Credits you have already purchased.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#FFDD59]">•</span>
                  <span className="text-gray-400"><span className="text-gray-300">Availability.</span> We use commercially reasonable efforts to keep the Site available, excluding planned maintenance and events beyond our reasonable control.</span>
                </li>
              </ul>
            </section>

            {/* Section 4 */}
            <section className="bg-gray-900/30 rounded-lg px-4 py-3 border border-gray-800/40">
              <h2 className="text-sm font-semibold text-white mb-2">
                4. Credit Validity, Expiry &amp; Consumption
              </h2>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-[#FFDD59]">•</span>
                  <span className="text-gray-400"><span className="text-gray-300">Expiry.</span> Pay-as-you-go Credits are valid for 30 days from the date of purchase. Subscription Credits are allotted at the start of each monthly billing cycle and are valid until the end of that cycle.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#FFDD59]">•</span>
                  <span className="text-gray-400"><span className="text-gray-300">Consumption.</span> Credits are consumed on a first-expiring-first (FIFO) basis, so that Credits closest to expiry are used first.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#FFDD59]">•</span>
                  <span className="text-gray-400"><span className="text-gray-300">No roll-over.</span> Unused Credits do not roll over after expiry and are forfeited. Credits have no cash value and are non-transferable.</span>
                </li>
              </ul>
            </section>

            {/* Section 5 */}
            <section className="bg-gray-900/30 rounded-lg px-4 py-3 border border-gray-800/40">
              <h2 className="text-sm font-semibold text-white mb-2">
                5. Subscriptions &amp; Auto-Renewal
              </h2>
              <p className="text-sm text-gray-400 leading-relaxed mb-3">
                Pro and Business are monthly subscriptions that automatically renew until cancelled. By subscribing, you authorise Persona, through our payment processor (Stripe), to charge your payment method the then-current monthly fee at the start of each billing cycle.
              </p>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-[#FFDD59]">•</span>
                  <span className="text-gray-400"><span className="text-gray-300">Cancellation.</span> You may cancel at any time through your account settings. Cancellation stops future renewals and takes effect at the end of the current paid period; you retain access to your subscription Credits until then.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#FFDD59]">•</span>
                  <span className="text-gray-400"><span className="text-gray-300">No mid-period refund.</span> No refunds or credits are issued for the current period upon cancellation.</span>
                </li>
              </ul>
            </section>

            {/* Section 6 */}
            <section className="bg-gray-900/30 rounded-lg px-4 py-3 border border-gray-800/40">
              <h2 className="text-sm font-semibold text-white mb-2">
                6. Payments, Taxes &amp; No-Refund Policy
              </h2>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-[#FFDD59]">•</span>
                  <span className="text-gray-400"><span className="text-gray-300">Billing.</span> All accounts are billed in advance through Stripe.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#FFDD59]">•</span>
                  <span className="text-gray-400"><span className="text-gray-300">Non-refundable.</span> Because Credits provide instant digital access to proprietary analysis, all payments are final and non-refundable - including for unused or expired Credits or change of mind - except where a refund is required by applicable law.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#FFDD59]">•</span>
                  <span className="text-gray-400"><span className="text-gray-300">Taxes.</span> All prices are exclusive of taxes. You are responsible for any taxes applicable in your jurisdiction, including any VAT/GST self-accounting or reverse-charge obligations. Persona charges applicable taxes only where legally required to do so.</span>
                </li>
              </ul>
            </section>

            {/* Section 7 */}
            <section className="bg-gray-900/30 rounded-lg px-4 py-3 border border-gray-800/40">
              <h2 className="text-sm font-semibold text-white mb-2">
                7. Nature of Analysis &amp; Data Source
              </h2>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-[#FFDD59]">•</span>
                  <span className="text-gray-400"><span className="text-gray-300">Predictive tool.</span> OPARA is an analytical tool that provides mathematical forecasts (e.g., "Expected Views," "Estimated CPV"). Results are for informational purposes only and do not constitute professional, financial, or investment advice.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#FFDD59]">•</span>
                  <span className="text-gray-400"><span className="text-gray-300">Data.</span> OPARA analyses publicly available profile data using AI-based algorithms. While we strive for high accuracy, we are not liable for discrepancies caused by social-media platform outages, changes, or other factors beyond our control.</span>
                </li>
              </ul>
            </section>

            {/* Section 8 */}
            <section className="bg-gray-900/30 rounded-lg px-4 py-3 border border-gray-800/40">
              <h2 className="text-sm font-semibold text-white mb-2">
                8. User Responsibilities &amp; Security
              </h2>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-[#FFDD59]">•</span>
                  <span className="text-gray-400"><span className="text-gray-300">Account security.</span> You are responsible for safeguarding your account credentials and for all activity that occurs under your account.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#FFDD59]">•</span>
                  <span className="text-gray-400"><span className="text-gray-300">Accuracy.</span> You represent that the influencer handles and budget data you input are accurate and that you have the right to use such data for analysis.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#FFDD59]">•</span>
                  <span className="text-gray-400"><span className="text-gray-300">Acceptable use.</span> You may not resell, redistribute, scrape, or reverse-engineer the Services, or use them to build a competing product.</span>
                </li>
              </ul>
            </section>

            {/* Section 9 */}
            <section className="bg-gray-900/30 rounded-lg px-4 py-3 border border-gray-800/40">
              <h2 className="text-sm font-semibold text-white mb-2">
                9. Intellectual Property
              </h2>
              <p className="text-sm text-gray-400 leading-relaxed">
                All content, software, algorithms, and the OPARA methodology are the exclusive property of <span className="text-gray-300">Persona Event And Marketing Solutions FZ LLE</span>. You are granted a limited, non-exclusive, non-transferable, revocable licence to use the reports for your internal business purposes. Scraping OPARA's proprietary data or reverse-engineering the Services is strictly prohibited.
              </p>
            </section>

            {/* Section 10 */}
            <section className="bg-gray-900/30 rounded-lg px-4 py-3 border border-gray-800/40">
              <h2 className="text-sm font-semibold text-white mb-2">
                10. Suspension &amp; Termination
              </h2>
              <p className="text-sm text-gray-400 leading-relaxed">
                We may suspend or terminate your access to the Services if you breach these Terms, misuse the Services, or where required by law. On termination, your right to use the Services ceases and any unused Credits are forfeited without refund, except where a refund is required by applicable law.
              </p>
            </section>

            {/* Section 11 */}
            <section className="bg-gray-900/30 rounded-lg px-4 py-3 border border-gray-800/40">
              <h2 className="text-sm font-semibold text-white mb-2">
                11. Limitation of Liability &amp; Indemnification
              </h2>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-[#FFDD59]">•</span>
                  <span className="text-gray-400"><span className="text-gray-300">No indirect damages.</span> To the maximum extent permitted by law, Persona shall not be liable for any indirect, incidental, or consequential damages, or for any commercial decisions made based on OPARA reports.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#FFDD59]">•</span>
                  <span className="text-gray-400"><span className="text-gray-300">Liability cap.</span> Persona's total aggregate liability arising out of or relating to the Services shall not exceed the total fees you paid to Persona in the twelve (12) months preceding the event giving rise to the claim.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#FFDD59]">•</span>
                  <span className="text-gray-400"><span className="text-gray-300">Indemnification.</span> You agree to indemnify and hold Persona harmless from any claims arising out of your use of the Services or your breach of these Terms.</span>
                </li>
              </ul>
            </section>

            {/* Section 12 */}
            <section className="bg-gray-900/30 rounded-lg px-4 py-3 border border-gray-800/40">
              <h2 className="text-sm font-semibold text-white mb-2">
                12. Governing Law &amp; Jurisdiction
              </h2>
              <p className="text-sm text-gray-400 leading-relaxed">
                This Agreement shall be governed by the laws of the <span className="text-gray-300">United Arab Emirates</span>. Any dispute arising out of or in connection with this Agreement shall be subject to the exclusive jurisdiction of the <span className="text-gray-300">DIFC Courts</span> (Dubai International Financial Centre).
              </p>
            </section>

            {/* Section 13 */}
            <section className="bg-gray-900/30 rounded-lg px-4 py-3 border border-gray-800/40">
              <h2 className="text-sm font-semibold text-white mb-2">
                13. Amendments
              </h2>
              <p className="text-sm text-gray-400 leading-relaxed">
                Persona may revise these Terms from time to time. Where reasonably practicable, we will notify you of material changes. Your continued use of the Services after the revised Terms take effect constitutes your acceptance of them.
              </p>
            </section>

          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
}

export default TermsPage;
